<template>
  <!-- Description Input -->
  <div class="w-full space-y-4">
    <div
      class="bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50 rounded-2xl p-6 flex flex-col gap-6 shadow-sm"
    >
      <div class="flex items-center gap-2 font-semibold">
        <span>Description</span>

        <!-- Tooltip only when hovering the icon -->
        <div class="relative group inline-block">
          <Icon
            icon="mdi:information-outline"
            class="w-4 h-4 text-gray-400 hover:text-mocha-mousse-600 cursor-help transition-colors"
          />
          <div
            class="absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 shadow-xl z-50"
          >
            Short summary describing your dataset and its content.
            <br />
            This field is <strong>mandatory</strong>.
          </div>
        </div>

        <label
          v-if="isDescriptionTooShort() && formChecked"
          class="text-red-500 text-sm font-normal"
        >
          Description is mandatory
        </label>
      </div>

      <!-- Textarea -->
      <div class="relative">
        <textarea
          id="description-editable-text"
          rows="10"
          class="p-3 w-full rounded-xl resize-none text-sm shadow-sm focus:ring-4 focus:ring-blue-100 transition-all bg-white/90 backdrop-blur-md"
          @keydown="handleKeydown"
          @input="handleInput"
          :class="[
            isDescriptionTooShort() && formChecked ? 'border-red-500' : '',
            encodedDescriptionLength === maxDescriptionLength
              ? 'border-red-500'
              : '',
          ]"
          :value="formState.description"
        ></textarea>

        <!-- Validation Messages -->
        <p
          v-if="isDescriptionTooShort() && formChecked"
          class="text-red-500 text-xs mt-1 flex items-center gap-1"
        >
          <Icon icon="mdi:alert-circle-outline" class="w-4 h-4" />
          Description must have at least 300 characters.
        </p>
        <p
          v-if="encodedDescriptionLength === maxDescriptionLength"
          class="text-red-500 text-xs mt-1 flex items-center gap-1"
        >
          <Icon icon="mdi:alert-circle-outline" class="w-4 h-4" />
          You’ve reached the maximum character limit!
        </p>

        <!-- Character counter -->
        <div
          class="absolute -bottom-6 right-0 flex items-center text-gray-400 text-xs gap-1"
        >
          <span id="description-char-count">{{
            encodedDescriptionLength
          }}</span>
          / {{ maxDescriptionLength }}
          <div class="relative group inline-block">
            <Icon
              icon="mdi:information-outline"
              class="w-3.5 h-3.5 text-gray-400 hover:text-mocha-mousse-500 cursor-help transition-colors"
            />
            <div
              class="absolute left-1/2 -translate-x-1/2 top-5 w-48 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 shadow-lg z-50"
            >
              Character count includes encoded characters. Special characters
              weigh more.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Navigation Buttons -->
  <div class="my-8 w-full flex justify-between items-center">
    <button
      class="px-6 py-2 cursor-pointer bg-gradient-to-r from-desert-flower-100 to-arabesque-200 font-medium rounded-lg shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
      @click="goToPrevPage"
    >
      <Icon
        icon="mdi:arrow-left"
        class="w-5 h-5 group-hover:translate-x-1 transition-transform"
      />
      <span>Back</span>
    </button>

    <button
      class="group relative px-8 py-3 cursor-pointer bg-gradient-to-r from-mint-green-100 to-periwinkle-100 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
      @click="goToNextPage"
      :disabled="isLoading"
    >
      <span>Next</span>
      <Icon
        icon="mdi:arrow-right"
        class="w-5 h-5 group-hover:translate-x-1 transition-transform"
      />
    </button>
  </div>
</template>

<script setup>
import { onMounted, ref, toRaw } from "vue";
import { useRouter } from "vue-router";
import { useFormStore } from "@/stores/FormStore";
import { useRouteStore } from "@/stores/ApplicationStore";

import { useLogger } from "@/stores/useLogger";
import { Icon } from "@iconify/vue";
import { usePreviewWorkflow } from "@/composables/usePreviewWorkflow";
import { useDescription } from "@/composables/useDescription";
import { useLoadingService } from "@/services/LoadingService"; // ✅ corrected path

const loadingSvc = useLoadingService();
const taskId = "description-preview";

const logger = useLogger();
logger.wireLifecycle();

const router = useRouter();
const routeStore = useRouteStore();
const formStore = useFormStore();
const formState = formStore.formState;

// UI state
const preview_result = ref();
const formChecked = ref(false);

// Workflow composables
const {
  isLoading,
  working_phase,
  working_phase_percentage,
  error,
  error_message,
  startPreviewAndPollStatus,
} = usePreviewWorkflow(taskId);

const {
  maxDescriptionLength,
  encodedDescriptionLength,
  createDescription,
  isDescriptionTooShort,
  handleInput,
  handleKeydown,
  countDescriptionEncodedCharacter,
} = useDescription(formState);

onMounted(async () => {
  if (routeStore.previousRoute.name === "new") {
    try {
      await sendToServerFileAndInfo();
    } catch (e) {
      console.error(e);
    }
  } else {
    countDescriptionEncodedCharacter();
  }
});

const sendToServerFileAndInfo = async () => {
  const formData = new FormData();

  // ✅ use store patch (avoid direct mutation)
  formStore.patch({ description: "" });

  // Reset flags
  formStore.patch({
    file_changed_from_preview: false,
    preview_needs_refresh: false
  });

  // Always send the file to regenerate the preview/description
  formData.append("file", formState.file);
  // ✅ read custom type from the file object (FileAndFolderLoader sets this)
  const customType = formState.file?.custom_type || "";
  formData.append("file_custom_type", customType);

  formData.append("filename", formState.filename);
  formData.append("title", `Dataset of ${formState.title}`);
  formData.append("upload_type", formState.upload_type);

  await startPreviewAndPollStatus(formData, (message) => {
    loadingSvc.update(taskId, { title: "Analyzing files…" });
    preview_result.value = message;

    // ✅ set structure via store API
    formStore.setStructure(message.file_structure || {});

    createDescription({
      n_files: message.n_files,
      extensions: message.extensions,
      dimension: message.dimension,
    });
  });
};

// Navigation
const goToPrevPage = () => {
  logger.log("button_clicked", {
    metadata: { button: "back_to_confirmation" },
  });
  router.push({ name: "new" });
};

const validateInputs = () => {
  formChecked.value = true;
  return !isDescriptionTooShort();
};

const goToNextPage = () => {
  logger.log("button_clicked", { metadata: { button: "to_submit" } });
  if (validateInputs()) {
    router.push({ name: "submit" });
  }
};
</script>
